import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Imovel } from '../../types/Property';
import { apiService } from '../../services/ApiService';
import { propertyService, SearchFilters, Property } from '../../services/PropertyService';
import { MultiStepPropertyForm } from '../Locador/MultiStepPropertyForm';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import { getModalidadeLabel, getModalidadeIcon, getImovelModalidades } from '../../utils/modalidadeUtils';
import { getOrderedImages } from '../../utils/mediaUtils';
import { formatDescriptionForDisplay } from '../../utils/descriptionFormatter';
import { removeAddressNumber } from '../../utils/addressUtils';
import { ImageDiagnostic } from './ImageDiagnostic';
import './AdminPage.css';

interface PendingImovel extends Omit<Imovel, 'locador'> {
  locador: {
    id: string;
    nome: string;
    email: string;
    telefone?: string;
    role?: string;
    ativo?: boolean;
    created_at?: string;
    updated_at?: string;
  };
  motivo_rejeicao?: string;
}

export const AdminPage: React.FC = () => {
  const [pendingImoveis, setPendingImoveis] = useState<PendingImovel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImovel, setSelectedImovel] = useState<PendingImovel | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [photoCarousels, setPhotoCarousels] = useState<{[key: string]: number}>({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [selectedImovelPhotos, setSelectedImovelPhotos] = useState<any[]>([]);
  const [selectedImovelId, setSelectedImovelId] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingImovel, setEditingImovel] = useState<PendingImovel | null>(null);
  const [activeFilter, setActiveFilter] = useState<'pendentes' | 'aprovados' | 'rejeitados' | 'alugados' | 'pausados' | 'vendidos'>('pendentes');
  const [allImoveis, setAllImoveis] = useState<PendingImovel[]>([]);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({});
  const [searchResults, setSearchResults] = useState<Property[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);

  const thumbnailScrollRef = useRef<HTMLDivElement>(null);

  const statusMatches = (
    filter: 'pendentes' | 'aprovados' | 'rejeitados' | 'alugados' | 'pausados' | 'vendidos',
    statusValue?: string
  ) => {
    const s = (statusValue || '').toLowerCase();
    switch (filter) {
      case 'pendentes':
        return s === 'pendente' || s === 'em_validacao' || s === 'analise';
      case 'aprovados':
        return s === 'aprovado' || s === 'disponivel' || s === 'ativo';
      case 'rejeitados':
        return s === 'rejeitado' || s === 'inativo';
      case 'alugados':
        return s === 'alugado' || s === 'indisponivel';
      case 'pausados':
        return s === 'pausado';
      case 'vendidos':
        return s === 'vendido';
      default:
        return false;
    }
  };




  useEffect(() => {
    fetchPendingImoveis();
    fetchAllImoveisForCounts();
  }, []);

  // Effect para rolar automaticamente para a foto selecionada
  useEffect(() => {
    if (thumbnailScrollRef.current && selectedImovelPhotos.length > 0) {
      const activeThumbnail = thumbnailScrollRef.current.querySelector('.photo-modal-thumbnail.active');
      
      if (activeThumbnail) {
        const container = thumbnailScrollRef.current;
        const thumbnailRect = activeThumbnail.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        const scrollLeft = thumbnailRect.left - containerRect.left - containerRect.width / 2 + thumbnailRect.width / 2;
        
        container.scrollTo({
          left: container.scrollLeft + scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [selectedPhotoIndex, selectedImovelPhotos]);

  // Função para carregar todos os imóveis para mostrar as quantidades nos botões
  const fetchAllImoveisForCounts = async () => {
    try {
      // Carregar todos os imóveis de todos os status para mostrar as quantidades
      const [pendentesRes, aprovadosRes, rejeitadosRes, alugadosRes, pausadosRes, vendidosRes] = await Promise.all([
        apiService.getImoveisPendentes(),
        apiService.getImoveisAprovados(),
        apiService.getImoveisRejeitados(),
        apiService.getImoveisAlugados(),
        apiService.getAdminImoveisByStatus('pausado'),
        apiService.getAdminImoveisByStatus('vendido')
      ]);

      const allImoveisList: any[] = [];
      
      if (pendentesRes.success && Array.isArray(pendentesRes.data)) {
        allImoveisList.push(...pendentesRes.data);
      }
      if (aprovadosRes.success && Array.isArray(aprovadosRes.data)) {
        allImoveisList.push(...aprovadosRes.data);
      }
      if (rejeitadosRes.success && Array.isArray(rejeitadosRes.data)) {
        allImoveisList.push(...rejeitadosRes.data);
      }
      if (alugadosRes.success && Array.isArray(alugadosRes.data)) {
        allImoveisList.push(...alugadosRes.data);
      }
      if (pausadosRes.success && Array.isArray(pausadosRes.data)) {
        allImoveisList.push(...pausadosRes.data);
      }
      if (vendidosRes.success && Array.isArray(vendidosRes.data)) {
        allImoveisList.push(...vendidosRes.data);
      }

      setAllImoveis(allImoveisList);
    } catch (error) {
      console.error('❌ Erro ao carregar imóveis para contagem:', error);
    }
  };


  const fetchPendingImoveis = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 AdminPage: Buscando imóveis pendentes...');
      // Usar o endpoint específico para imóveis pendentes
      const response = await apiService.getImoveisPendentes();
      
      if (response.success && response.data) {
        if (Array.isArray(response.data)) {
          setPendingImoveis(response.data);
          setAllImoveis(response.data); // Para compatibilidade com filtros
          console.log('✅ Imóveis pendentes carregados:', response.data.length);
        } else {
          console.error('❌ Dados não são um array:', response.data);
          setAllImoveis([]);
          setPendingImoveis([]);
        }
      } else {
        console.error('❌ Erro na API:', response.message);
        setError(response.message || 'Erro ao carregar imóveis pendentes');
      }
    } catch (err: any) {
      console.error('❌ Erro na requisição:', err);
      setError(err.message || 'Erro ao carregar imóveis');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (imovelId: string) => {
    setActionLoading(imovelId);
    
    try {
      const response = await apiService.approveImovel(imovelId);
      
      if (response.success) {
        // Atualizar o status do imóvel na lista geral
        setAllImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'disponivel' as any } : imovel
        ));
        // Atualizar ou remover o imóvel da lista atual
        setPendingImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'disponivel' as any } : imovel
        ));
        alert('Imóvel aprovado com sucesso!');
        // Recarregar a lista se necessário
        if (activeFilter === 'pausados') {
          filterImoveis('pausados');
        }
      } else {
        alert('Erro ao aprovar imóvel: ' + response.message);
      }
    } catch (err: any) {
      alert('Erro ao aprovar imóvel: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (imovelId: string) => {
    if (!rejectReason.trim()) {
      alert('Por favor, informe o motivo da rejeição');
      return;
    }

    setActionLoading(imovelId);
    
    try {
      const response = await apiService.rejectImovel(imovelId, rejectReason);
      
      if (response.success) {
        // Atualizar o status do imóvel na lista geral
        setAllImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'rejeitado' as any, motivo_rejeicao: rejectReason } : imovel
        ));
        // Atualizar ou remover o imóvel da lista atual
        setPendingImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'rejeitado' as any, motivo_rejeicao: rejectReason } : imovel
        ));
        setShowRejectModal(false);
        setRejectReason('');
        setSelectedImovel(null);
        alert('Imóvel rejeitado com sucesso!');
        // Recarregar a lista se necessário
        if (activeFilter === 'pausados') {
          filterImoveis('pausados');
        }
      } else {
        alert('Erro ao rejeitar imóvel: ' + response.message);
      }
    } catch (err: any) {
      alert('Erro ao rejeitar imóvel: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePausar = async (imovelId: string) => {
    setActionLoading(imovelId);
    
    try {
      const response = await apiService.pausarImovel(imovelId);
      
      if (response.success) {
        // Atualizar o status do imóvel na lista geral
        setAllImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'pausado' as any } : imovel
        ));
        // Atualizar o status na lista atual
        setPendingImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'pausado' as any } : imovel
        ));
        alert('Imóvel pausado com sucesso!');
        // Recarregar a lista se necessário
        if (activeFilter !== 'pausados') {
          filterImoveis(activeFilter);
        }
      } else {
        alert('Erro ao pausar imóvel: ' + response.message);
      }
    } catch (err: any) {
      alert('Erro ao pausar imóvel: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarcarComoVendido = async (imovelId: string) => {
    if (!window.confirm('Tem certeza que deseja marcar este imóvel como vendido?')) {
      return;
    }

    setActionLoading(imovelId);
    
    try {
      const response = await apiService.marcarComoVendido(imovelId);
      
      if (response.success) {
        // Atualizar o status do imóvel na lista geral
        setAllImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'vendido' as any } : imovel
        ));
        // Atualizar o status na lista atual
        setPendingImoveis(prev => prev.map(imovel => 
          imovel.id === imovelId ? { ...imovel, status: 'vendido' as any } : imovel
        ));
        alert('Imóvel marcado como vendido com sucesso!');
        // Recarregar a lista se necessário
        if (activeFilter !== 'vendidos') {
          filterImoveis(activeFilter);
        }
      } else {
        alert('Erro ao marcar imóvel como vendido: ' + response.message);
      }
    } catch (err: any) {
      alert('Erro ao marcar imóvel como vendido: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (imovelId: string) => {
    setActionLoading(imovelId);
    try {
      const response = await apiService.deleteImovel(imovelId);
      if (response.success) {
        // Remover o imóvel de todas as listas
        setPendingImoveis(prev => prev.filter(imovel => imovel.id !== imovelId));
        setAllImoveis(prev => prev.filter(imovel => imovel.id !== imovelId));
        setShowDeleteModal(false);
        setSelectedImovel(null);
        alert('Imóvel excluído com sucesso!');
      } else {
        alert('Erro ao excluir imóvel: ' + response.message);
      }
    } catch (err: any) {
      alert('Erro ao excluir imóvel: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    fetchPendingImoveis(); // Recarregar a lista de imóveis pendentes
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  // Funções para edição de imóveis
  const handleEditImovel = (imovel: PendingImovel) => {
    setEditingImovel(imovel);
    setShowEditForm(true);
  };

  const handleEditSuccess = () => {
    setShowEditForm(false);
    setEditingImovel(null);
    setShowPhotoModal(false); // Fechar modal de fotos também
    setSelectedImovelPhotos([]);
    setSelectedPhotoIndex(0);
    setSelectedImovelId(null);
    fetchPendingImoveis(); // Recarregar a lista de imóveis pendentes
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingImovel(null);
  };

  // Função para filtrar imóveis por status
  const filterImoveis = async (filter: 'pendentes' | 'aprovados' | 'rejeitados' | 'alugados' | 'pausados' | 'vendidos') => {
    setActiveFilter(filter);
    setShowSearchResults(false);
    setSearchResults([]);

    try {
      if (filter === 'pendentes') {
        const response = await apiService.getImoveisPendentes();
        const list = (response && response.success && Array.isArray(response.data)) ? response.data : [];
        setPendingImoveis(list as any);
        setAllImoveis(list as any);
        return;
      }

      if (filter === 'pausados' || filter === 'vendidos') {
        const status = filter === 'pausados' ? 'pausado' : 'vendido';
        const response = await apiService.getAdminImoveisByStatus(status);
        const list = (response && response.success && Array.isArray(response.data)) ? response.data : [];
        setPendingImoveis(list as any);
        setAllImoveis(list as any);
        return;
      }

      const tryMatrix: Record<typeof filter, Array<Record<string, string>>> = {
        pendentes: [ { status: 'pendente' } ],
        aprovados: [
          { status: 'aprovado' },
          { status: 'disponivel' },
          { situacao: 'aprovado' },
          { situacao: 'disponivel' },
          { status: 'ativo' },
        ],
        rejeitados: [ { status: 'rejeitado' }, { situacao: 'rejeitado' } ],
        alugados: [ { status: 'alugado' }, { situacao: 'alugado' } ],
        pausados: [ { status: 'pausado' } ],
        vendidos: [ { status: 'vendido' } ],
      } as any;

      const { list, used } = await apiService.tryAdminStatuses(tryMatrix[filter]);
      console.log('🔎 Resultado admin/status', { filter, used, quantidade: list.length });

      if (filter === 'aprovados' && (!Array.isArray(list) || list.length === 0)) {
        console.log('ℹ️ Fallback para rota pública /imoveis?status=disponivel');
        const publicResp = await apiService.getImoveis({ page: 1, limit: 100 });
        const publicList = (publicResp && publicResp.success && Array.isArray((publicResp as any).data?.imoveis))
          ? (publicResp as any).data.imoveis
          : Array.isArray((publicResp as any).data) ? (publicResp as any).data : [];
        setPendingImoveis(publicList as any);
        setAllImoveis(publicList as any);
        return;
      }

      setPendingImoveis(list as any);
      setAllImoveis(list as any);
    } catch (e) {
      console.error('Erro ao buscar imóveis por status:', e);
      setPendingImoveis([]);
      setAllImoveis([]);
    }
  };

  // Função para obter o título baseado no filtro ativo
  const getSectionTitle = () => {
    if (showSearchResults) {
      return `Resultados da Busca (${searchResults.length} imóveis encontrados)`;
    }
    
    switch (activeFilter) {
      case 'pendentes':
        return 'Imóveis Aguardando Aprovação';
      case 'aprovados':
        return 'Imóveis Aprovados';
      case 'rejeitados':
        return 'Imóveis Rejeitados';
      case 'pausados':
        return 'Imóveis Pausados';
      case 'vendidos':
        return 'Imóveis Vendidos';
      case 'alugados':
        return 'Imóveis Alugados';
      default:
        return 'Imóveis Aguardando Aprovação';
    }
  };

  // Função para buscar imóveis com filtros
  const searchProperties = async (filters: SearchFilters) => {
    setSearchLoading(true);
    setSearchFilters(filters);
    
    try {
      const response = await propertyService.searchProperties(filters);
      
      if (response.success) {
        setSearchResults(response.data.imoveis);
        setShowSearchResults(true);
      } else {
        console.error('Erro na busca:', response.message);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Erro na busca de imóveis:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para limpar busca e voltar aos filtros de status
  const clearSearch = () => {
    setSearchResults([]);
    setShowSearchResults(false);
    setSearchFilters({});
  };

  // Função para verificar se é PendingImovel
  const isPendingImovel = (imovel: PendingImovel | Property): imovel is PendingImovel => {
    return 'locador_id' in imovel;
  };

  const openRejectModal = (imovel: PendingImovel) => {
    setSelectedImovel(imovel);
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setSelectedImovel(null);
  };

  const openDeleteModal = (imovel: PendingImovel) => {
    setSelectedImovel(imovel);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedImovel(null);
  };

  const formatCurrency = (value: string | number) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const nextPhoto = (imovelId: string, totalPhotos: number) => {
    setPhotoCarousels(prev => ({
      ...prev,
      [imovelId]: ((prev[imovelId] || 0) + 1) % totalPhotos
    }));
  };

  const prevPhoto = (imovelId: string, totalPhotos: number) => {
    setPhotoCarousels(prev => ({
      ...prev,
      [imovelId]: prev[imovelId] === 0 ? totalPhotos - 1 : (prev[imovelId] || 0) - 1
    }));
  };

  // Funções para o modal de fotos
  const openPhotoModal = (imovel: PendingImovel, photoIndex: number) => {
    console.log('🔍 Abrindo modal de fotos:', {
      imovelId: imovel.id,
      photoIndex,
      midias: imovel.midias,
      midiasLength: imovel.midias?.length,
      firstPhoto: imovel.midias?.[0],
      selectedPhoto: imovel.midias?.[photoIndex]
    });
    
    if (imovel.midias && imovel.midias.length > 0) {
      console.log('🖼️ Configurando modal com fotos:', imovel.midias.map(m => ({ id: m.id, url: m.url, titulo: m.titulo })));
      setSelectedImovelPhotos(imovel.midias);
      setSelectedPhotoIndex(photoIndex);
      setSelectedImovelId(imovel.id);
      setShowPhotoModal(true);
    } else {
      console.log('❌ Nenhuma mídia encontrada para o imóvel');
    }
  };

  const closePhotoModal = () => {
    setShowPhotoModal(false);
    setSelectedImovelPhotos([]);
    setSelectedPhotoIndex(0);
    setSelectedImovelId(null);
  };

  const nextPhotoModal = () => {
    if (selectedImovelPhotos.length > 0) {
      setSelectedPhotoIndex((prev) => (prev + 1) % selectedImovelPhotos.length);
    }
  };

  const prevPhotoModal = () => {
    if (selectedImovelPhotos.length > 0) {
      setSelectedPhotoIndex((prev) => (prev - 1 + selectedImovelPhotos.length) % selectedImovelPhotos.length);
    }
  };

  // Função para remover foto
  const removePhoto = async (photoId: string) => {
    if (!selectedImovelId) return;
    
    if (!window.confirm('Tem certeza que deseja remover esta foto? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      console.log('🗑️ Removendo foto:', photoId);
      
      // Chamar API para excluir a mídia
      const response = await apiService.deleteMidia(photoId);
      
      if (response.success) {
        console.log('✅ Foto removida com sucesso via API');
        
        // Atualizar a lista de fotos do modal
        setSelectedImovelPhotos(prev => {
          const newPhotos = prev.filter(photo => photo.id !== photoId);
          
          // Ajustar o índice se necessário
          if (selectedPhotoIndex >= newPhotos.length) {
            setSelectedPhotoIndex(Math.max(0, newPhotos.length - 1));
          }
          
          return newPhotos;
        });

        // Atualizar TODAS as listas de imóveis (pendentes, aprovados, rejeitados, alugados)
        const updateImovelLists = (prevList: PendingImovel[]) => 
          prevList.map(imovel => {
            if (imovel.id === selectedImovelId) {
              return {
                ...imovel,
                midias: imovel.midias.filter(photo => photo.id !== photoId)
              };
            }
            return imovel;
          });

        setPendingImoveis(updateImovelLists);
        setAllImoveis(updateImovelLists);

        // Se não há mais fotos, fechar o modal
        if (selectedImovelPhotos.length <= 1) {
          closePhotoModal();
        }

        alert('Foto removida com sucesso!');
      } else {
        console.error('❌ Erro na API ao remover foto:', response.message);
        alert('Erro ao remover foto: ' + response.message);
      }
    } catch (error) {
      console.error('❌ Erro ao remover foto:', error);
      alert('Erro ao remover foto. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Carregando imóveis pendentes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Painel Administrativo</h1>
          <p>Gerencie imóveis pendentes de aprovação</p>
          <div className="header-actions">
            <button onClick={() => setShowCreateForm(true)} className="btn-create">
              Cadastrar Imóvel
            </button>
            <button onClick={() => setShowSearchForm(!showSearchForm)} className="btn-search">
              Buscar Imóveis
            </button>
            <button 
              onClick={fetchPendingImoveis} 
              className="btn-refresh"
              title="Atualizar lista"
            >
              Atualizar
            </button>
            <button 
              onClick={() => setShowDiagnostic(!showDiagnostic)} 
              className="btn-diagnostic"
              title="Diagnóstico de imagens"
            >
              🔍 Diagnóstico
            </button>
          </div>
        </div>

        {/* Filtros de Status */}
        <div className="status-filters">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${activeFilter === 'pendentes' ? 'active' : ''}`}
              onClick={() => filterImoveis('pendentes')}
            >
              Pendentes ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('pendentes', imovel.status)).length : 0})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'aprovados' ? 'active' : ''}`}
              onClick={() => filterImoveis('aprovados')}
            >
              Aprovados ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('aprovados', imovel.status)).length : 0})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'rejeitados' ? 'active' : ''}`}
              onClick={() => filterImoveis('rejeitados')}
            >
              Rejeitados ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('rejeitados', imovel.status)).length : 0})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'alugados' ? 'active' : ''}`}
              onClick={() => filterImoveis('alugados')}
            >
              Alugados ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('alugados', imovel.status)).length : 0})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'pausados' ? 'active' : ''}`}
              onClick={() => filterImoveis('pausados')}
            >
              Pausados ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('pausados', imovel.status)).length : 0})
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'vendidos' ? 'active' : ''}`}
              onClick={() => filterImoveis('vendidos')}
            >
              Vendidos ({Array.isArray(allImoveis) ? allImoveis.filter((imovel: any) => statusMatches('vendidos', imovel.status)).length : 0})
            </button>
          </div>
        </div>

        {/* Modal de Busca */}
        {showSearchForm && (
          <div className="search-modal-overlay" onClick={() => setShowSearchForm(false)}>
            <div className="search-modal" onClick={(e) => e.stopPropagation()}>
              <div className="search-modal-header">
                <h3>Buscar Imóveis</h3>
                <button 
                  className="search-modal-close" 
                  onClick={() => setShowSearchForm(false)}
                >
                  ✕
                </button>
              </div>
              
              <div className="search-modal-content">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  const filters: SearchFilters = {
                    cidade: formData.get('cidade') as string || undefined,
                    estado: formData.get('estado') as string || undefined,
                    bairro: formData.get('bairro') as string || undefined,
                    tipo: formData.get('tipo') as string || undefined,
                    quartos: formData.get('quartos') ? parseInt(formData.get('quartos') as string) : undefined,
                    valor_min: formData.get('valor_min') ? parseFloat(formData.get('valor_min') as string) : undefined,
                    valor_max: formData.get('valor_max') ? parseFloat(formData.get('valor_max') as string) : undefined,
                    search: formData.get('search') as string || undefined,
                  };
                  searchProperties(filters);
                  setShowSearchForm(false);
                }}>
                  <div className="search-form-grid">
                    <div className="search-form-group">
                      <label>Cidade</label>
                      <input type="text" name="cidade" placeholder="Ex: Balneário Camboriú" />
                    </div>
                    <div className="search-form-group">
                      <label>Estado</label>
                      <input type="text" name="estado" placeholder="Ex: SC" />
                    </div>
                    <div className="search-form-group">
                      <label>Bairro</label>
                      <input type="text" name="bairro" placeholder="Ex: Centro" />
                    </div>
                    <div className="search-form-group">
                      <label>Tipo</label>
                      <select name="tipo">
                        <option value="">Todos</option>
                        <option value="apartamento">Apartamento</option>
                        <option value="casa">Casa</option>
                        <option value="kitnet">Kitnet</option>
                        <option value="studio">Studio</option>
                        <option value="loft">Loft</option>
                        <option value="duplex">Duplex</option>
                        <option value="cobertura">Cobertura</option>
                        <option value="terreno">Terreno</option>
                        <option value="comercial">Comercial</option>
                        <option value="galpao">Galpão</option>
                      </select>
                    </div>
                    <div className="search-form-group">
                      <label>Quartos</label>
                      <select name="quartos">
                        <option value="">Todos</option>
                        <option value="1">1+</option>
                        <option value="2">2+</option>
                        <option value="3">3+</option>
                        <option value="4">4+</option>
                      </select>
                    </div>
                    <div className="search-form-group">
                      <label>Valor Mínimo</label>
                      <input type="number" name="valor_min" placeholder="Ex: 1000" />
                    </div>
                    <div className="search-form-group">
                      <label>Valor Máximo</label>
                      <input type="number" name="valor_max" placeholder="Ex: 5000" />
                    </div>
                    <div className="search-form-group full-width">
                      <label>Busca por Texto</label>
                      <input type="text" name="search" placeholder="Buscar por título ou descrição..." />
                    </div>
                  </div>

                  <div className="search-modal-actions">
                    <button type="button" onClick={clearSearch} className="btn-clear-search">
                      Limpar
                    </button>
                    <button type="submit" className="btn-search-submit" disabled={searchLoading}>
                      {searchLoading ? 'Buscando...' : 'Buscar'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
        

        {error && (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchPendingImoveis} className="btn-retry">
              Tentar Novamente
            </button>
          </div>
        )}

        {!loading && !error && pendingImoveis.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">
              
            </div>
            <h3>Nenhum imóvel pendente</h3>
            <p>Todos os imóveis foram analisados!</p>
          </div>
        )}

        {!loading && !error && (showSearchResults ? searchResults.length > 0 : pendingImoveis.length > 0) && (
          <>

            <div className="pending-imoveis">
              <h2>{getSectionTitle()}</h2>
              {showSearchResults && (
                <div className="search-info">
                  <p>Filtros aplicados: {Object.entries(searchFilters).filter(([_, v]) => v).map(([k, v]) => `${k}: ${v}`).join(', ') || 'Nenhum filtro específico'}</p>
                </div>
              )}
              <div className="imoveis-grid">
                {(showSearchResults ? searchResults : pendingImoveis).map(imovel => (
                  <div key={imovel.id} className="imovel-card">
                    <div className="imovel-header">
                      <h3>{imovel.titulo}</h3>
                      <div className="imovel-header-right">
                        <span className="imovel-code">CÓD: {imovel.codigo}</span>
                        <span className={`status-badge status-${imovel.status}`}>
                          {imovel.status}
                        </span>
                      </div>
                    </div>

                    <div className="imovel-info">
                      <div className="info-grid">
                        <div className="info-row">
                          <span className="label">Tipo:</span>
                          <span className="value">{imovel.tipo_imovel || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Cidade:</span>
                          <span className="value">{imovel.cidade || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Estado:</span>
                          <span className="value">{imovel.estado || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Bairro:</span>
                          <span className="value">{imovel.bairro || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">CEP:</span>
                          <span className="value">{imovel.cep || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Endereço:</span>
                          <span className="value">{removeAddressNumber(imovel.endereco) || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Área Útil:</span>
                          <span className="value">{imovel.area_util ? `${imovel.area_util}m²` : '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Quartos:</span>
                          <span className="value">{imovel.quartos || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Banheiros:</span>
                          <span className="value">{imovel.banheiros || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Vagas Garagem:</span>
                          <span className="value">{imovel.vagas_garagem || '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Valor Aluguel:</span>
                          <span className="value price">{imovel.valor_aluguel && imovel.valor_aluguel > 0 ? formatCurrency(imovel.valor_aluguel) : '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Valor Venda:</span>
                          <span className="value price">{('valor_venda' in imovel && imovel.valor_venda && imovel.valor_venda > 0) ? formatCurrency(imovel.valor_venda) : '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Valor Condomínio:</span>
                          <span className="value">{imovel.valor_condominio && parseFloat(imovel.valor_condominio.toString()) > 0 ? formatCurrency(imovel.valor_condominio) : '--'}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">Valor IPTU:</span>
                          <span className="value">{imovel.valor_iptu && parseFloat(imovel.valor_iptu.toString()) > 0 ? formatCurrency(imovel.valor_iptu) : '--'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="imovel-features">
                      <div className="features-grid">
                        {imovel.aceita_pet && <span className="feature"> Aceita Pet</span>}
                        {imovel.mobiliado && <span className="feature"> Mobiliado</span>}
                        {imovel.ar_condicionado && <span className="feature">Ar Condicionado</span>}
                        {imovel.internet && <span className="feature"> Internet</span>}
                      </div>
                      {imovel.comodidades.length > 0 && (
                        <div className="comodidades">
                          <span className="label">Comodidades:</span>
                          <span className="value">{imovel.comodidades.join(', ')}</span>
                        </div>
                      )}
                      {(() => {
                        const modalidades = getImovelModalidades(imovel);
                        return modalidades.length > 0 ? (
                          <div className="modalidades-aluguel">
                            <span className="label">Modalidades de Aluguel:</span>
                            <div className="modalidades-values">
                              {modalidades.map((modalidade, index) => (
                                <span key={modalidade.id || index} className="modalidade-item">
                                  {getModalidadeIcon(modalidade.tipo)} {modalidade.nome || getModalidadeLabel(modalidade.tipo)}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    <div className="imovel-description">
                      {imovel.descricao ? (
                        <div className="markdown-content">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => <p className="markdown-paragraph">{children}</p>,
                              h1: ({ children }) => <h1 className="markdown-h1">{children}</h1>,
                              h2: ({ children }) => <h2 className="markdown-h2">{children}</h2>,
                              h3: ({ children }) => <h3 className="markdown-h3">{children}</h3>,
                              ul: ({ children }) => <ul className="markdown-ul">{children}</ul>,
                              ol: ({ children }) => <ol className="markdown-ol">{children}</ol>,
                              li: ({ children }) => <li className="markdown-li">{children}</li>,
                              strong: ({ children }) => <strong className="markdown-strong">{children}</strong>,
                              em: ({ children }) => <em className="markdown-em">{children}</em>,
                              code: ({ children }) => <code className="markdown-code">{children}</code>,
                              blockquote: ({ children }) => <blockquote className="markdown-blockquote">{children}</blockquote>,
                              a: ({ href, children }) => <a href={href} className="markdown-link" target="_blank" rel="noopener noreferrer">{children}</a>,
                            }}
                          >
                            {formatDescriptionForDisplay(imovel.descricao)}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p>--</p>
                      )}
                    </div>


                    {/* Seção de Fotos */}
                    <div className="imovel-photos">
                      <h4>Fotos do Imóvel</h4>
                      {imovel.midias && imovel.midias.length > 0 ? (
                        <div className="photos-carousel">
                          <div className="carousel-container">
                            <button 
                              className="carousel-btn prev"
                              onClick={() => prevPhoto(imovel.id, imovel.midias.length)}
                              disabled={imovel.midias.length <= 1}
                            >
                              ‹
                            </button>
                            
                            <div className="carousel-photos">
                              {getOrderedImages(imovel as Imovel).map((midia, index) => {
                                const imageUrl = getImageUrl(midia.url);
                                
                                return (
                                  <div 
                                    key={midia.id}
                                    className={`carousel-photo ${index === (photoCarousels[imovel.id] || 0) ? 'active' : ''}`}
                                  >
                                    <img 
                                      src={imageUrl}
                                      alt={midia.titulo || `Foto ${index + 1}`}
                                      crossOrigin="anonymous"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => isPendingImovel(imovel) && openPhotoModal(imovel, index)}
                                      onError={(e) => {
                                        handleImageError(e, 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbSBu8m8gZW5jb250cmFkYTwvdGV4dD48L3N2Zz4=');
                                        // Remover o onError após primeira tentativa para evitar loop
                                        (e.target as HTMLImageElement).onerror = null;
                                      }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                            
                            <button 
                              className="carousel-btn next"
                              onClick={() => nextPhoto(imovel.id, imovel.midias.length)}
                              disabled={imovel.midias.length <= 1}
                            >
                              ›
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="no-photos">
                          <div className="no-photos-placeholder">
                            📷
                          </div>
                          <p>Nenhuma foto disponível</p>
                        </div>
                      )}
                    </div>

                    <div className="locador-info">
                      <h4>Informações do Locador</h4>
                      <div className="info-row">
                        <span className="label">Nome:</span>
                        <span className="value">{imovel.locador.nome}</span>
                      </div>
                      <div className="info-row">
                        <span className="label">Email:</span>
                        <span className="value">{imovel.locador.email}</span>
                      </div>
                      {imovel.locador.telefone && (
                        <div className="info-row">
                          <span className="label">Telefone:</span>
                          <span className="value">{imovel.locador.telefone}</span>
                        </div>
                      )}
                    </div>

                    <div className="imovel-meta">
                      <span className="created-at">
                        Criado em: {formatDate(imovel.created_at)}
                      </span>
                      {(imovel as any).visualizacoes !== undefined && (
                        <span className="visualizacoes-count">
                          • Visualizações: {(imovel as any).visualizacoes || 0}
                        </span>
                      )}
                    </div>

                    {!showSearchResults && (
                      <div className="imovel-actions">
                        {/* Botões de Editar e Excluir - disponíveis em todas as abas */}
                        {isPendingImovel(imovel) && (
                          <>
                            <button
                              onClick={() => handleEditImovel(imovel)}
                              className="btn-edit"
                              disabled={actionLoading === imovel.id}
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => openDeleteModal(imovel)}
                              className="btn-delete"
                              disabled={actionLoading === imovel.id}
                            >
                              Excluir
                            </button>
                          </>
                        )}
                        
                        {/* Botões para imóveis pausados */}
                        {imovel.status === 'pausado' && isPendingImovel(imovel) && (
                          <>
                            <button
                              onClick={() => handleApprove(imovel.id)}
                              className="btn-approve"
                              disabled={actionLoading === imovel.id}
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => openRejectModal(imovel)}
                              className="btn-reject"
                              disabled={actionLoading === imovel.id}
                            >
                              Rejeitar
                            </button>
                          </>
                        )}
                        
                        {/* Botões para imóveis pendentes */}
                        {imovel.status === 'pendente' && isPendingImovel(imovel) && (
                          <>
                            <button
                              onClick={() => handleApprove(imovel.id)}
                              className="btn-approve"
                              disabled={actionLoading === imovel.id}
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => openRejectModal(imovel)}
                              className="btn-reject"
                              disabled={actionLoading === imovel.id}
                            >
                              Rejeitar
                            </button>
                          </>
                        )}
                        
                        {/* Botão para pausar - disponível para qualquer status exceto pausado e vendido */}
                        {imovel.status !== 'pausado' && imovel.status !== 'vendido' && (
                          <button
                            onClick={() => handlePausar(imovel.id)}
                            className="btn-pause"
                            disabled={actionLoading === imovel.id}
                            title="Pausar imóvel"
                          >
                            {actionLoading === imovel.id ? 'Pausando...' : 'Pausar'}
                          </button>
                        )}
                        
                        {/* Botão para marcar como vendido - disponível para imóveis disponíveis */}
                        {imovel.status === 'disponivel' && (
                          <button
                            onClick={() => handleMarcarComoVendido(imovel.id)}
                            className="btn-vendido"
                            disabled={actionLoading === imovel.id}
                            title="Marcar como vendido"
                          >
                            {actionLoading === imovel.id ? 'Marcando...' : 'Marcar como Vendido'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}


        {/* Modal de Rejeição */}
        {showRejectModal && selectedImovel && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Rejeitar Imóvel</h3>
                <button onClick={closeRejectModal} className="modal-close">✕</button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja rejeitar o imóvel <strong>"{selectedImovel.titulo}"</strong>?</p>
                <div className="form-group">
                  <label htmlFor="reject-reason">Motivo da rejeição:</label>
                  <textarea
                    id="reject-reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Informe o motivo da rejeição..."
                    rows={4}
                    className="form-textarea"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={closeRejectModal} className="btn-cancel">
                  Cancelar
                </button>
                <button
                  onClick={() => handleReject(selectedImovel.id)}
                  className="btn-confirm-reject"
                  disabled={actionLoading === selectedImovel.id || !rejectReason.trim()}
                >
                  Rejeitar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Exclusão */}
        {showDeleteModal && selectedImovel && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Excluir Imóvel</h3>
                <button onClick={closeDeleteModal} className="modal-close">✕</button>
              </div>
              <div className="modal-body">
                <p>Tem certeza que deseja <strong>excluir permanentemente</strong> o imóvel <strong>"{selectedImovel.titulo}"</strong>?</p>
                <div style={{
                  padding: '16px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  marginTop: '16px'
                }}>
                  <p style={{ margin: '0', color: '#ef4444', fontWeight: '600' }}>
                    ⚠️ Esta ação é irreversível!
                  </p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#b8b8b8' }}>
                    <li>O imóvel será removido permanentemente</li>
                    <li>Todas as fotos e arquivos serão deletados</li>
                    <li>Esta ação não pode ser desfeita</li>
                  </ul>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={closeDeleteModal} className="btn-cancel">
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(selectedImovel.id)}
                  className="btn-confirm-delete"
                  disabled={actionLoading === selectedImovel.id}
                >
                  {actionLoading === selectedImovel.id ? 'Excluindo...' : 'Excluir Permanentemente'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Formulário de Criação de Imóvel */}
        {showCreateForm && (
          <MultiStepPropertyForm
            onClose={handleCancelCreate}
            onSuccess={handleCreateSuccess}
            adminMode={true}
          />
        )}

        {/* Formulário de Edição de Imóvel */}
        {showEditForm && editingImovel && (
          <MultiStepPropertyForm
            onClose={handleCancelEdit}
            onSuccess={handleEditSuccess}
            editingImovel={editingImovel as any}
            adminMode={true}
          />
        )}

        {/* Componente de Diagnóstico */}
        {showDiagnostic && (
          <div className="diagnostic-overlay" onClick={() => setShowDiagnostic(false)}>
            <div className="diagnostic-modal" onClick={(e) => e.stopPropagation()}>
              <div className="diagnostic-header">
                <h3>Diagnóstico de Imagens</h3>
                <button 
                  className="diagnostic-close" 
                  onClick={() => setShowDiagnostic(false)}
                >
                  ✕
                </button>
              </div>
              <div className="diagnostic-content">
                <ImageDiagnostic />
              </div>
            </div>
          </div>
        )}

        {/* Modal de Visualização de Fotos */}
        {showPhotoModal && selectedImovelPhotos.length > 0 && (
          <div className="photo-modal-overlay" onClick={closePhotoModal}>
            <div className="photo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="photo-modal-header">
                <h3>Fotos do Imóvel</h3>
                <div className="photo-modal-header-actions">
                  <button 
                    className="photo-modal-remove" 
                    onClick={() => removePhoto(selectedImovelPhotos[selectedPhotoIndex].id)}
                    title="Remover foto"
                  >
                    🗑️
                  </button>
                  <button className="photo-modal-close" onClick={closePhotoModal}>
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="photo-modal-content">
                <div className="photo-modal-main">
                  <button 
                    className="photo-modal-nav prev" 
                    onClick={prevPhotoModal}
                    disabled={selectedImovelPhotos.length <= 1}
                  >
                    ‹
                  </button>
                  
                  <div className="photo-modal-image-container">
                    {selectedImovelPhotos.length > 0 && selectedImovelPhotos[selectedPhotoIndex] ? (
                      <>
                        <img 
                          src={getImageUrl(selectedImovelPhotos[selectedPhotoIndex].url)}
                          alt={selectedImovelPhotos[selectedPhotoIndex].titulo || `Foto ${selectedPhotoIndex + 1}`}
                          className="photo-modal-image"
                          onError={(e) => {
                            console.log('❌ Erro ao carregar imagem no modal:', e);
                            handleImageError(e, 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlbSBu8m8gZW5jb250cmFkYTwvdGV4dD48L3N2Zz4=');
                            // Remover o onError após primeira tentativa para evitar loop
                            (e.target as HTMLImageElement).onerror = null;
                          }}
                          onLoad={() => {
                            console.log('✅ Imagem carregada com sucesso no modal');
                          }}
                        />
                      </>
                    ) : (
                      <div className="no-image-placeholder">
                        <p>Nenhuma imagem disponível</p>
                      </div>
                    )}
                  </div>
                  
                  <button 
                    className="photo-modal-nav next" 
                    onClick={nextPhotoModal}
                    disabled={selectedImovelPhotos.length <= 1}
                  >
                    ›
                  </button>
                </div>
                
                {selectedImovelPhotos.length > 1 && (
                  <div className="photo-modal-thumbnails-wrapper">
                    <button 
                      className="photo-thumbnail-nav prev-thumbnav"
                      onClick={() => {
                        const newIndex = selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : selectedImovelPhotos.length - 1;
                        setSelectedPhotoIndex(newIndex);
                      }}
                      title="Foto anterior"
                    >
                      ‹
                    </button>
                    
                    <div className="photo-modal-thumbnails" ref={thumbnailScrollRef}>
                      {selectedImovelPhotos.map((photo, index) => (
                        <div key={photo.id} className="photo-modal-thumbnail-container">
                          <img
                            src={getImageUrl(photo.url)}
                            alt={photo.titulo || `Thumbnail ${index + 1}`}
                            className={`photo-modal-thumbnail ${index === selectedPhotoIndex ? 'active' : ''}`}
                            onClick={() => setSelectedPhotoIndex(index)}
                            onError={(e) => {
                              handleImageError(e, 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2RkZCIvPjwvc3ZnPg==');
                              (e.target as HTMLImageElement).onerror = null;
                            }}
                          />
                          <button
                            className="photo-modal-thumbnail-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              removePhoto(photo.id);
                            }}
                            title="Remover esta foto"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <button 
                      className="photo-thumbnail-nav next-thumbnav"
                      onClick={() => {
                        const newIndex = selectedPhotoIndex < selectedImovelPhotos.length - 1 ? selectedPhotoIndex + 1 : 0;
                        setSelectedPhotoIndex(newIndex);
                      }}
                      title="Próxima foto"
                    >
                      ›
                    </button>
                  </div>
                )}
                
                <div className="photo-modal-counter">
                  {selectedPhotoIndex + 1} de {selectedImovelPhotos.length}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
